import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useSesion } from "../lib/sesion";
import { Aviso, Campo } from "../componentes/piezas";
import { useIdioma } from "../lib/idioma";

interface EstadoRegistro {
  abierto: boolean;
  exigeCodigo: boolean;
}

export default function Registro() {
  const { t, idioma } = useIdioma();
  const { registrarClub } = useSesion();
  const [datos, setDatos] = useState({
    nombreClub: "",
    ciudad: "",
    nombre: "",
    email: "",
    password: "",
    codigo: "",
  });
  const [repetir, setRepetir] = useState("");
  const [acepta, setAcepta] = useState(false);
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);

  const estado = useQuery({
    queryKey: ["estado-registro"],
    queryFn: () => api<EstadoRegistro>("/auth/registro"),
  });

  const cambiar = (campo: string, valor: string) => setDatos((d) => ({ ...d, [campo]: valor }));

  async function enviar(e: FormEvent) {
    e.preventDefault();
    if (datos.password !== repetir) {
      setError(t("rg.noCoinciden"));
      return;
    }
    setError("");
    setEnviando(true);
    try {
      /*
       * Con el idioma de esta pantalla: es el que el club va a usar con sus
       * socios y el que usamos para escribirle. Sin esto, un club que llegaba
       * por la web en aleman nacia con el idioma vacio y recibia en castellano
       * el primer correo que le mandabamos.
       */
      await registrarClub({
        ...datos,
        ciudad: datos.ciudad || null,
        aceptaCondiciones: true,
        idioma,
      });
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : t("rg.fallo"));
    } finally {
      setEnviando(false);
    }
  }

  if (estado.data && !estado.data.abierto) {
    return (
      <div className="pantalla-login">
        <div className="caja-login">
          <h1 style={{ fontSize: "1.25rem", marginBottom: 10 }}>{t("rg.cerrado")}</h1>
          <p className="suave" style={{ marginTop: 0 }}>
            {t("rg.cerradoTexto")}
          </p>
          <Link className="boton bloque" to="/">
            {t("rg.volverAEntrar")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pantalla-login">
      <form className="caja-login" onSubmit={enviar}>
        <div className="fila" style={{ marginBottom: 18 }}>
          <div className="logo-circulo">+</div>
          <div>
            <h1 style={{ fontSize: "1.25rem" }}>{t("rg.titulo")}</h1>
            <div className="suave pequeno">{t("rg.subtitulo")}</div>
          </div>
        </div>

        <Aviso tipo="error">{error}</Aviso>

        <Campo etiqueta={t("rg.nombreClub")}>
          <input
            value={datos.nombreClub}
            onChange={(e) => cambiar("nombreClub", e.target.value)}
            required
            autoFocus
            placeholder={t("rg.nombreClubEjemplo")}
          />
        </Campo>

        <Campo etiqueta={t("rg.ciudad")}>
          <input value={datos.ciudad} onChange={(e) => cambiar("ciudad", e.target.value)} />
        </Campo>

        <hr style={{ border: 0, borderTop: "1px solid var(--borde)", margin: "18px 0" }} />

        <Campo etiqueta={t("rg.tuNombre")}>
          <input value={datos.nombre} onChange={(e) => cambiar("nombre", e.target.value)} required />
        </Campo>

        <Campo etiqueta={t("rg.tuEmail")} ayuda={t("rg.tuEmailAyuda")}>
          <input
            type="email"
            value={datos.email}
            onChange={(e) => cambiar("email", e.target.value)}
            autoComplete="username"
            required
          />
        </Campo>

        <Campo etiqueta={t("rg.contrasena")} ayuda={t("rg.minimo")}>
          <input
            type="password"
            value={datos.password}
            onChange={(e) => cambiar("password", e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
          />
        </Campo>

        <Campo etiqueta={t("rg.repite")}>
          <input
            type="password"
            value={repetir}
            onChange={(e) => setRepetir(e.target.value)}
            autoComplete="new-password"
            required
          />
        </Campo>

        {estado.data?.exigeCodigo && (
          <Campo
            etiqueta={t("rg.codigo")}
            ayuda={t("rg.codigoAyuda")}
          >
            <input value={datos.codigo} onChange={(e) => cambiar("codigo", e.target.value)} required />
          </Campo>
        )}

        {/* El servidor lo exige tambien: una casilla que solo vive en el
            navegador la salta cualquiera con dos clics. */}
        <label className="casilla" style={{ marginTop: 6 }}>
          <input type="checkbox" checked={acepta} onChange={(e) => setAcepta(e.target.checked)} />
          <span>
            {t("rg.acepto")} <Link to="/aviso-legal">{t("rg.avisoLegal")}</Link> {t("rg.yLa")}{" "}
            <Link to="/privacidad">{t("rg.privacidad")}</Link>.
          </span>
        </label>

        <button className="boton grande bloque" disabled={enviando || !acepta} style={{ marginTop: 6 }}>
          {enviando ? t("rg.creando") : t("rg.crear")}
        </button>

        <p className="centrado pequeno suave" style={{ marginBottom: 0 }}>
          {t("rg.yaTeneis")} <Link to="/">{t("rg.entrar")}</Link>
        </p>
      </form>
    </div>
  );
}
